// src/components/Layout.js
import React, { useMemo, useState, createContext } from "react";
import {
  CssBaseline,
  Box,
  Container,
  Button,
  IconButton,
} from "@mui/material";
import { LogoutRounded } from "@mui/icons-material";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { usePermissions } from "../permissions/PermissionsProvider";
import { ACTIONS } from "../permissions/definition";

export const HeaderActionsContext = createContext({
  setHeaderActions: () => { },
});

const Layout = ({ children, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();
  const [headerActions, setHeaderActions] = useState(null);
  const { can } = usePermissions();

  const userDisplayName = useMemo(() => {
    const email = user?.email || "";
    if (!email.includes("@")) return "";
    const [rawName] = email.split("@");
    if (!rawName) return "";

    return rawName
      .split(/[._-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }, [user?.email]);

  const handleSignOut = () => {
    signOut(auth).then(() => {
      navigate("/login");
    });
  };

  const navItems = useMemo(() => {
    const items = [
      { text: "Projects", path: "/", action: ACTIONS.NAV_VIEW_PROJECTS },
      { text: "Tasks", path: "/tasks", action: ACTIONS.NAV_VIEW_TASKS },
      { text: "Schedule", path: "/schedule", action: ACTIONS.NAV_VIEW_SCHEDULE },
      { text: "Clients", path: "/clients", action: ACTIONS.NAV_VIEW_CLIENTS },
      {
        text: "Analytics",
        path: "/analytics",
        action: ACTIONS.NAV_VIEW_ANALYTICS,
      },
    ];

    return items.filter((item) => item.action === null || can(item.action));
  }, [can]);

  const activePath = useMemo(() => {
    if (location.pathname.startsWith("/tasks")) return "/tasks";
    if (location.pathname.startsWith("/schedule")) return "/schedule";
    if (location.pathname.startsWith("/clients")) return "/clients";
    if (location.pathname.startsWith("/analytics")) return "/analytics";
    return "/";
  }, [location.pathname]);

  return (
    <HeaderActionsContext.Provider value={{ setHeaderActions }}>
      <Box sx={{ minHeight: "100vh", bgcolor: "#fff" }}>
        <CssBaseline />

        <Box
          sx={{
            backgroundColor: "#fff",
            boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
            mb: 5,
            position: "sticky",
            top: 0,
            zIndex: 1000,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              sx={{
                width: "100%",
                maxWidth: "1500px",
                margin: "0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                pl: 0,
                pr: 4,
                py: 2.5,
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => navigate("/")}
                aria-label="Go to Orders"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  ml: -3.5,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  p: 0,
                }}
              >
                <Box
                  component="img"
                  src="/logo.png"
                  alt="Logo"
                  sx={{
                    height: 64,
                    width: "auto",
                    maxHeight: 64,
                    transform: "scale(1.2)",
                    transformOrigin: "left center",
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                {userDisplayName ? (
                  <Box
                    component="span"
                    sx={{
                      px: 2.25,
                      py: 0.9,
                      borderRadius: "999px",
                      backgroundColor: "#e6f0ff",
                      color: "#1d4ed8",
                      fontWeight: 500,
                      fontSize: "0.95rem",
                      letterSpacing: "0.01em",
                      display: "inline-flex",
                      alignItems: "center",
                      lineHeight: 1,
                      mr: 1.25,
                    }}
                  >
                    {userDisplayName}
                  </Box>
                ) : null}
                {headerActions}
                <IconButton
                  size="large"
                  onClick={handleSignOut}
                  data-tooltip-id="logout-tooltip"
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: "#e53935",
                    color: "#fff",
                    boxShadow: "0 6px 16px rgba(229,57,53,0.35)",
                    "&:hover": { backgroundColor: "#d32f2f" },
                  }}
                >
                  <LogoutRounded />
                </IconButton>
                <ReactTooltip
                  id="logout-tooltip"
                  place="bottom"
                  effect="solid"
                  className="tooltip-centered"
                >
                  Logout
                </ReactTooltip>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              borderTop: "1px solid #f0f1f5",
              mt: 1.5,
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: "1500px",
                margin: "0 auto",
                display: "flex",
                alignItems: "center",
                gap: 3,
                px: 4,
              }}
            >
              {navItems.map((item) => {
                const isActive = activePath === item.path;
                return (
                  <Button
                    key={item.text}
                    onClick={() => navigate(item.path)}
                    disableRipple
                    sx={{
                      position: "relative",
                      textTransform: "none",
                      fontSize: "1rem",
                      color: isActive ? "#2d6bff" : "#7a7a7a",
                      borderRadius: 0,
                      paddingY: 1.2,
                      paddingX: 0.5,
                      minWidth: "auto",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: "3px",
                        backgroundColor: isActive ? "#2d6bff" : "transparent",
                        borderRadius: "3px",
                        transition: "background-color 180ms ease",
                      },
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: "#2d6bff",
                      },
                    }}
                  >
                    {item.text}
                  </Button>
                );
              })}
            </Box>
          </Box>
        </Box>

        <Container maxWidth={false} sx={{ px: 4, pb: 4 }}>
          {children}
        </Container>
      </Box>
    </HeaderActionsContext.Provider>
  );
};

export default Layout;
